import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

interface PrintInvoiceResult {
  document: {
    id: string;
    file_name: string;
    status: string;
  };
  downloadUrl: string;
}

/**
 * Hook to generate and download an invoice PDF using the document template engine.
 *
 * Calls POST /billing/invoices/:id/generate-pdf which:
 * 1. Loads the invoice data (line items, client, etc.)
 * 2. Renders it through the default (or specified) invoice template
 * 3. Generates a PDF via Puppeteer
 * 4. Stores it in MinIO
 * 5. Returns a presigned download URL
 *
 * The browser then opens the URL to download/print the PDF.
 */
export function usePrintInvoice() {
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<PrintInvoiceResult> => {
      const { data } = await apiClient.post<ApiResponse<PrintInvoiceResult>>(
        `/billing/invoices/${invoiceId}/generate-pdf`,
        { async: false },
      );
      return data.data;
    },
  });
}
